import Cookies from "js-cookie";
import React, { FC, useEffect, useState } from "react";
import { Auth } from "aws-amplify";
import { Redirect } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import AppShellLayout from "../layouts/AppShellLayout";
import BaseLayout from "../layouts/BaseLayout";
import GlobalAdvertiserInit from "../components/GlobalAdvertiserInit";
import Loader from "../components/elements/Loader";
import api from "../api";
import { ROUTE_PATHS } from "../types/RoutePaths";
import { ReduxApplicationState } from "../types/ReduxTypes/ReduxApplicationState";
import { SentryRoute } from "../sentry";
import { ensureAgencyForUser } from "../utils/ensureAgencyForUser";
import { setAgency, setBrandgenPlayBooks } from "../redux/reducers/agency";
import { setSession } from "../redux/reducers/session";
import { setUser, setUserAccess } from "../redux/reducers/user";

type AuthGuardRouteProps = {
  exact?: boolean;
  path: string;
  component: React.ComponentType<any>;
  useNewLayout?: boolean;
  title?: string;
  subtitle?: string;
  adminOnly?: boolean;
};

const fetchAuthenticatedUser = async () => {
  const authenticatedUser = await Auth.currentAuthenticatedUser();
  return authenticatedUser;
};

const fetchUserAccess = async () => {
  const { payload } = await api.getCurrentUserAccess();
  return payload;
};

const AuthGuardRoute: FC<AuthGuardRouteProps> = ({
  // eslint-disable-next-line @typescript-eslint/naming-convention
  component: Component,
  useNewLayout,
  title,
  subtitle,
  adminOnly,
  ...rest
}) => {
  const dispatch = useDispatch();

  const isAuthenticated: boolean = useSelector((state: ReduxApplicationState) => state.session.isAuthenticated);
  const agency = useSelector((state: ReduxApplicationState) => state.agency);
  const session = useSelector((state: ReduxApplicationState) => state.session.session);
  const isAdmin = useSelector((state: ReduxApplicationState) => state.user.isAdmin);
  const isRoleLoaded = useSelector((state: ReduxApplicationState) => state.user.isRoleLoaded);

  const [isUserAuthenticatedChecking, setIsUserAuthenticatedChecking] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    const checkAuthStatus = async () => {
      try {
        setIsUserAuthenticatedChecking(true);
        const user = await Auth.currentSession();

        if (user) {
          const newSession = {
            accessToken: user.getAccessToken().getJwtToken(),
            idToken: user.getIdToken().getJwtToken(),
            refreshToken: user.getRefreshToken().getToken(),
          };
          if (!session) {
            dispatch(setSession(newSession));
          }
          Cookies.set("ACCESS_TOKEN", newSession.accessToken);
        }

        if ((session || user) && !agency.name) {
          const authenticatedUser = await fetchAuthenticatedUser();
          const associatedAgency = await ensureAgencyForUser(authenticatedUser);
          if (cancelled) {
            return;
          }
          if (associatedAgency) {
            dispatch(setAgency(associatedAgency));
          }
          dispatch(setUser(authenticatedUser.username));
          dispatch(setBrandgenPlayBooks());
          try {
            const access = await fetchUserAccess();
            dispatch(
              setUserAccess({
                role: access?.role ?? "user",
                isAdmin: Boolean(access?.isAdmin),
                canManageRoles: Boolean(access?.canManageRoles),
              }),
            );
          } catch  {
            dispatch(setUserAccess({ role: "user", isAdmin: false, canManageRoles: false }));
          }
          setIsUserAuthenticatedChecking(false);
        } else if (session && !isRoleLoaded) {
          try {
            const access = await fetchUserAccess();
            dispatch(
              setUserAccess({
                role: access?.role ?? "user",
                isAdmin: Boolean(access?.isAdmin),
                canManageRoles: Boolean(access?.canManageRoles),
              }),
            );
          } catch  {
            dispatch(setUserAccess({ role: "user", isAdmin: false, canManageRoles: false }));
          }
          setIsUserAuthenticatedChecking(false);
        } else {
          setIsUserAuthenticatedChecking(false);
        }
      } catch (error) {
        if (error === "No current user") {
          // eslint-disable-next-line no-console
          console.log("User is not Authenticated!");
        } else {
          // eslint-disable-next-line no-console
          console.log("error:", error);
        }
      } finally {
        if (!cancelled) {
          setIsUserAuthenticatedChecking(false);
        }
      }
    };
    checkAuthStatus();
  }, [agency.name, dispatch, isRoleLoaded, session]);

  return (
    <SentryRoute
      render={(props) => {
        if (!isUserAuthenticatedChecking && !isAuthenticated) {
          return <Redirect to={{ pathname: "/" }} />;
        }
        if (!isUserAuthenticatedChecking && adminOnly && isRoleLoaded && !isAdmin) {
          return <Redirect to={{ pathname: ROUTE_PATHS.DASHBOARD }} />;
        }
        const child =
          isUserAuthenticatedChecking || (adminOnly && !isRoleLoaded) ? <Loader /> : <Component {...props} />;
        const content = (
          <>
            <GlobalAdvertiserInit />
            {child}
          </>
        );
        return useNewLayout ? (
          <AppShellLayout key={rest.path as string} title={title ?? ""} subtitle={subtitle}>
            {content}
          </AppShellLayout>
        ) : (
          <BaseLayout>{content}</BaseLayout>
        );
      }}
      {...rest}
    />
  );
};

export default AuthGuardRoute;
